"use client";

import { useLocale, useTranslations } from "next-intl";
import { ExternalLink, BookOpen } from "lucide-react";

const AFFILIATE_LINKS: Record<string, string> = {
  pt: "https://amzn.to/4rcQMRf",
  "pt-PT": "https://amzn.to/4rcQMRf",
  es: "https://amzn.to/4s7RHmr",
  en: "https://amzn.to/3MuNxpf",
};

const BOOK_COVERS: Record<string, string> = {
  pt: "https://m.media-amazon.com/images/I/71da0pEmHoL._SY466_.jpg",
  "pt-PT": "https://m.media-amazon.com/images/I/71da0pEmHoL._SY466_.jpg",
  es: "https://m.media-amazon.com/images/I/71xHehcs2wL._SY466_.jpg",
  en: "https://m.media-amazon.com/images/I/81m1gW3w%2BEL._SY466_.jpg",
};

const DEFAULT_LINK = AFFILIATE_LINKS.en;
const DEFAULT_COVER = BOOK_COVERS.en;

export function BookBanner() {
  const t = useTranslations();
  const locale = useLocale();

  const affiliateLink = AFFILIATE_LINKS[locale] || DEFAULT_LINK;
  const bookCover = BOOK_COVERS[locale] || DEFAULT_COVER;

  return (
    <section className="w-full bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 dark:from-primary/10 dark:via-primary/15 dark:to-primary/10 border-y border-primary/20">
      <div className="container mx-auto px-4 py-8 md:py-10">
        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 max-w-4xl mx-auto">
          <a
            href={affiliateLink}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="shrink-0 group"
          >
            <div className="relative w-28 md:w-36 aspect-[2/3] rounded-lg overflow-hidden shadow-lg group-hover:shadow-xl transition-shadow duration-300 ring-2 ring-primary/20 group-hover:ring-primary/40">
              <img
                src={bookCover}
                alt={t("book_banner_title")}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            </div>
          </a>

          <div className="flex-1 text-center md:text-start">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
              <BookOpen className="w-5 h-5 text-primary" />
              <span className="text-xs uppercase tracking-wider text-primary font-semibold">
                {t("book_banner_label")}
              </span>
            </div>

            <h3 className="text-xl md:text-2xl font-serif font-bold text-foreground mb-2">
              {t("book_banner_title")}
            </h3>

            <p className="text-sm md:text-base text-muted-foreground mb-4 leading-relaxed">
              {t("book_banner_description")}
            </p>

            <a
              href={affiliateLink}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF9900] hover:bg-[#E88A00] text-black font-semibold rounded-full shadow-md hover:shadow-lg transition-all duration-300 text-sm md:text-base"
            >
              {t("book_banner_cta")}
              <ExternalLink className="w-4 h-4" />
            </a>

            <p className="text-xs text-muted-foreground/70 mt-3 italic">
              {t("book_banner_affiliate_note")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
